"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import Announcement from "../components/Announcement";

const translations = {
  en: {
    navigation: {
      home: "Home",
      products: "Products",
      forChefs: "For Chefs",
      contact: "Contact Us",
      certificates: "Certificates",
      about: "About Us"
    },
    language: {
      english: "English",
      turkish: "Türkçe"
    }
  },
  tr: {
    navigation: {
      home: "Ana Sayfa",
      products: "Ürünler",
      forChefs: "Şefler İçin",
      contact: "İletişim",
      certificates: "Sertifikalar",
      about: "Hakkımızda"
    },
    language: {
      english: "English",
      turkish: "Türkçe"
    }
  }
};

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [language, setLanguage] = useState<"en" | "tr">("en");
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage") as "en" | "tr";
    if (storedLanguage) {
      setLanguage(storedLanguage);
    }
  }, []);

  const t = translations[language];

 const handleLanguageChange = async (newLanguage: "en" | "tr") => {
  // First set the language in localStorage
  localStorage.setItem("selectedLanguage", newLanguage);
  
  // Update state
  setLanguage(newLanguage);
  setIsOpen(false);
  setIsMenuOpen(false);

  // Dispatch storage event manually since it doesn't fire for same-page changes
  window.dispatchEvent(new Event('storage'));
   window.dispatchEvent(new Event('languageChange'));
  
  if (pathname !== '/') {
    await router.replace('/');
  }
}

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  useEffect(() => {
    if (!isMenuOpen) {
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
        setLastScrollY(currentScrollY);
      };

      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [lastScrollY, isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
        isVisible ? "transform translate-y-0" : "transform -translate-y-full"
      } bg-brown-1000 border-b border-brown-200`}
    >
      <Announcement />
      <div className="relative flex justify-between items-center px-10 md:px-20 py-4">
        {/* Logo Section */}
        <div className="flex-1 flex items-center justify-start">
          <Link href="/">
            <img
              src="https://i.ibb.co/sCBKDtY/logo10.png"
              width={100}
              height={100}
              alt="Logo"
              className="cursor-pointer"
              style={{ paddingTop: '10px', paddingBottom: '10px' }}
            />
          </Link>
        </div>

        {/* Hamburger Menu for Mobile */}
        <div className="flex md:hidden items-center">
          <button
            onClick={toggleMenu}
            aria-label="Toggle menu"
            className="text-brown-000 focus:outline-none"
          >
            <FontAwesomeIcon
              icon={isMenuOpen ? faTimes : faBars}
              className="h-6 w-6"
            />
          </button>
        </div>

        {/* Full-Screen Overlay Menu */}
        <div
          style={{
            display: isMenuOpen ? "flex" : "none",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "#2F1B01",
            color: "white",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          {/* Close Icon */}
          <button
            onClick={toggleMenu}
            aria-label="Close menu"
            className="absolute top-4 right-9 text-brown-000 focus:outline-none"
          >
            <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
          </button>

          {/* Mobile Links Container */}
          <div className="px-12 text-2xl font-[500] mt-24 flex flex-col text-left w-full h-full gap-8">
            <Link href="/" onClick={toggleMenu} className={`${pathname === "/" ? "text-brown-100" : "text-brown-000 hover:text-brown-200"}`}>
              {t.navigation.home}
            </Link>
            <Link href="/products" onClick={toggleMenu} className={`${pathname === "/products" ? "text-brown-100" : "text-brown-000 hover:text-brown-200"}`}>
              {t.navigation.products}
            </Link>
            <Link href="/for-chefs" onClick={toggleMenu} className={`${pathname === "/for-chefs" ? "text-brown-100" : "text-brown-000 hover:text-brown-200"}`}>
              {t.navigation.forChefs}
            </Link>
            <Link href="/contact" onClick={toggleMenu} className={`${pathname === "/contact" ? "text-brown-100" : "text-brown-000 hover:text-brown-200"}`}>
              {t.navigation.contact}
            </Link>
            <Link href="/certificates" onClick={toggleMenu} className={`${pathname === "/certificates" ? "text-brown-100" : "text-brown-000 hover:text-brown-200"}`}>
              {t.navigation.certificates}
            </Link>
            <Link href="/about" onClick={toggleMenu} className={`${pathname === "/about" ? "text-brown-100" : "text-brown-000 hover:text-brown-200"}`}>
              {t.navigation.about}
            </Link>

            {/* Mobile Language Selector */}
            <div className="flex space-x-4 mt-4">
              <button
                className="text-brown-000 hover:text-brown-200 px-2 py-1"
                onClick={() => handleLanguageChange("en")}
              >
                {translations.en.language.english}
              </button>
              <button
                className="text-brown-000 hover:text-brown-200 px-2 py-1"
                onClick={() => handleLanguageChange("tr")}
              >
                {translations.tr.language.turkish}
              </button>
            </div>

            {/* Mobile Instagram Icon */}
            <a
              href="https://instagram.com/botechgida"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brown-000 hover:text-brown-200 mt-4"
            >
              <FontAwesomeIcon icon={faInstagram} className="h-5 w-5" />
            </a>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex flex-1 justify-center items-center font-[400] space-x-6 text-brown-100 h-full whitespace-nowrap text-sm">
          <Link href="/" className={`${pathname === "/" ? "bg-brown-800 font-[500] h-full px-2 flex items-center transition-colors duration-200 text-brown-100 hover:text-brown-000" : "px-4 py-2 rounded hover:text-brown-000 inline-flex items-center transition-colors duration-100"}`}>
            {t.navigation.home}
          </Link>
          <Link href="/products" className={`${pathname === "/products" ? "bg-brown-800 font-[500] h-full px-2 flex items-center transition-colors duration-200 text-brown-100 hover:text-brown-000" : "px-4 py-2 rounded hover:text-brown-000 inline-flex items-center transition-colors duration-100"}`}>
            {t.navigation.products}
          </Link>
          <Link href="/for-chefs" className={`${pathname === "/for-chefs" ? "bg-brown-800 font-[500] h-full px-2 flex items-center transition-colors duration-200 text-brown-100 hover:text-brown-000" : "px-4 py-2 rounded hover:text-brown-000 inline-flex items-center transition-colors duration-100"}`}>
            {t.navigation.forChefs}
          </Link>
          <Link href="/contact" className={`${pathname === "/contact" ? "bg-brown-800 font-[500] h-full px-2 flex items-center transition-colors duration-200 text-brown-100 hover:text-brown-000" : "px-4 py-2 rounded hover:text-brown-000 inline-flex items-center transition-colors duration-100"}`}>
            {t.navigation.contact}
          </Link>
          <Link href="/certificates" className={`${pathname === "/certificates" ? "bg-brown-800 font-[500] h-full px-2 flex items-center transition-colors duration-200 text-brown-100 hover:text-brown-000" : "px-4 py-2 rounded hover:text-brown-000 inline-flex items-center transition-colors duration-100"}`}>
            {t.navigation.certificates}
          </Link>
          <Link href="/about" className={`${pathname === "/about" ? "bg-brown-800 font-[500] h-full px-2 flex items-center transition-colors duration-200 text-brown-100 hover:text-brown-000" : "px-4 py-2 rounded hover:text-brown-000 inline-flex items-center transition-colors duration-100"}`}>
            {t.navigation.about}
          </Link>
        </div>

        {/* Desktop Language Selector and Instagram Icon */}
        <div className="hidden md:flex flex-1 justify-end items-center space-x-6">
          <a
            href="https://instagram.com/botechgida"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brown-100 hover:text-brown-000 transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faInstagram} className="h-5 w-5" />
          </a>

          <div className="language-selector relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="bg-transparent text-brown-000 px-2 py-1 rounded text-sm border-none focus:outline-none"
            >
              {language === "en" ? t.language.english : t.language.turkish}
            </button>

            {isOpen && (
              <div className="absolute top-full mt-2 w-32 bg-brown-1000 z-50 right-0">
                <ul>
                  <li
                    className="px-4 py-2 text-white hover:bg-brown-700 cursor-pointer transition-transform duration-300"
                    onClick={() => handleLanguageChange("en")}
                  >
                    {translations.en.language.english}
                  </li>
                  <li
                    className="px-4 py-2 text-white hover:bg-brown-700 cursor-pointer transition-transform duration-300"
                    onClick={() => handleLanguageChange("tr")}
                  >
                    {translations.tr.language.turkish}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
